import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

// Actions
import { selectExploreSite } from "../../../actions/map-view/media-sites/ExploreSitesSelectionActions";

// Utils & constants
import { useUrlKeyCheck } from "../../../mavin/utils/hooks/HooksUtil";
import {
  LocalStorageKeys,
  MediaSourceTypeStrings,
  PatrikaSellerId,
  PriceModeConstants,
  UrlKeys,
} from "../../../constants/GeneralConstants";
import { getItem } from "../../../utils/localstorage";
import { getMediaCostForCampaign } from "../../../utils/MediaUtils";

// Urls
import { RedirectTo } from "../../../urls/PageLinksURL";

// Components
import Spinner from "../../../components/spinner/Spinner";
import {
  InfoCell,
  ImpressionsCell,
  LtsCell,
} from "../../../components/campaign-media-table-row/MediaRow";
import { OtsAndReachHeader } from "../../../components/table/OtsAndReachHeader";
import TableHeaders from "../../../components/table/TableHeaders";
import Pagination from "../../../components/pagination/Pagination";

// Constants
const otsHeaderInfo = [
  {
    displayText: "TG Impressions",
    className: "bg-light-green",
    percentage: "25",
  },
  {
    displayText: "Total Impressions",
    className: "bg-light-blue",
    percentage: "100",
  },
];

function getInventoryMediaTableHeaders(campaignId) {
  const tableHeaders = [
    {
      title: {
        displayName: "Site Name",
        className: "col-5 align-middle",
      },
      subTitle: {
        displayName: "Media type, Dimensions, Lighting, Region",
        className: "sub-text",
      },
    },

    {
      element: (
        <OtsAndReachHeader headerInfo={otsHeaderInfo} subText="Monthly" />
      ),
    },
    {
      title: {
        displayName: "LTS",
        className: "col-2 text-center align-middle",
      },
    },
  ];

  // Adding additional header to table for "addMediaToCampaign" button
  if (campaignId) {
    tableHeaders.push({
      title: {
        displayName: "Action",
        className: "text-center align-middle",
      },
    });
  }

  return tableHeaders;
}

function ActionButtonCell({
  campaignId,
  campaignMediaInfo,
  isSiteAdded,
  dispatchFunction,
  isLoading,
  isOrgMedia,
}) {
  const dispatch = useDispatch();

  const { campaignPlanning, cityAnalyser } = UrlKeys;
  // plan and city-analyser key will not present in campaign analyser page url
  // so this will return true for campaign planner and city analyser page
  // if the plan is present in url then its is from campaign planning page
  const isDisabled = useUrlKeyCheck(campaignPlanning, cityAnalyser);

  const {
    mediaId,
    pricing = {},
    cityId,
    duration,
    sellerId,
  } = campaignMediaInfo || {};
  const costForCampaignPrice = getMediaCostForCampaign(pricing, duration);
  const { price } = pricing || {};

  const mediaTypeString = isOrgMedia
    ? MediaSourceTypeStrings.orgMedia
    : MediaSourceTypeStrings.sellerMedia;

  // checking is patrika specific media
  const isPatrikaMedia = sellerId === PatrikaSellerId;

  const campaignMedia = [
    {
      price: price || 0,
      sellerPrice: costForCampaignPrice || 0,
      priceFactor: 1,
      mediaId,
      priceMode: PriceModeConstants.FIXED,
      cityId,
      mediaTypeStr: mediaTypeString,
    },
  ];

  const buttonLabel = isSiteAdded ? "Added" : "Add";
  const buttonClassName = isSiteAdded
    ? "btn-outline-success"
    : "btn-outline-primary";

  return (
    <td className="align-middle text-center">
      <button
        className={`btn ${buttonClassName} add-button shadow-none`}
        onClick={() => dispatch(dispatchFunction(campaignId, campaignMedia))}
        disabled={isLoading || isSiteAdded || !isDisabled}
      >
        {isSiteAdded && <i className="fa fa-check mx-1"></i>}
        {buttonLabel}
        {isLoading && <Spinner className="ml-2 spinner-border-sm" />}
      </button>
    </td>
  );
}

function ExploreSitesTableRow({
  media,
  campaignId,
  isOrgMedia,
  isPatrikaSpecific,
  citySummary = {},
}) {
  const { mediaId, sellerId } = media;
  const user = JSON.parse(getItem(LocalStorageKeys.USER)) || {};
  const { orgId } = user;

  // Selector State
  const isLoading = useSelector(
    (state) => state.exploreSitesSelection.selectedSitesLoadingMap[mediaId]
  );
  const mediaList = useSelector(
    (state) => state.mapViewMediaSites.filteredMediaSites
  );

  // city duration
  const { duration } = citySummary || {};

  // adding cityDuration to media for mantaray budget calculation
  // since we have to add media to city under a campaign
  if (isOrgMedia && campaignId) {
    media.duration = duration;
  }

  // adding cityDuration days to media for patrika
  if (isPatrikaSpecific && campaignId) {
    media.duration = duration;
  }

  const isSiteAdded = mediaList.some((media) => media.mediaId === mediaId);

  // Redirect Url
  const redirectUrl = isOrgMedia
    ? RedirectTo.orgMediaDetailPageUrl
        .replace(":orgId", orgId)
        .replace(":mediaId", mediaId)
    : RedirectTo.sellerMediaDetailPageUrl
        .replace(":sellerId", sellerId)
        .replace(":mediaId", mediaId);

  return (
    <tr>
      <InfoCell
        mediaElementStyle={"p-2"}
        media={media}
        redirectUrl={redirectUrl}
      />

      <ImpressionsCell
        impressionsElementStyle="p-2 align-middle"
        media={media}
      />

      <LtsCell ltsElementClass={"p-2 text-center align-middle"} media={media} />

      {campaignId && (
        <ActionButtonCell
          campaignId={campaignId}
          campaignMediaInfo={media}
          isSiteAdded={isSiteAdded}
          dispatchFunction={selectExploreSite}
          isLoading={isLoading}
          isOrgMedia={isOrgMedia}
        />
      )}
    </tr>
  );
}

function ExploreSitesTable({
  mediaList,
  pagination,
  loadCityMedia,
  isOrgMedia,
  isPatrikaSpecific,
}) {
  const { id: campaignId } = useParams();

  const { summary: citySummary = {} } = useSelector(
    (state) => state.campaignCityReport.citySummary
  );

  const { campaignPlanning, cityAnalyser } = UrlKeys;
  // plan and city-analyser key will not present in campaign analyser page url
  // so this will return true for campaign planner and city analyser page
  // if the plan is present in url then its is from campaign planning page
  const isDisabled = useUrlKeyCheck(campaignPlanning, cityAnalyser);

  if (mediaList.length < 1) {
    return null;
  }

  const tableHeaders = getInventoryMediaTableHeaders(campaignId);

  const { totalCount, pageNo, pageSize } = pagination;

  return (
    <div className="table-responsive" disabled={!isDisabled}>
      <table className="table table-media">
        <TableHeaders tableHeaders={tableHeaders} headerClass={"thead"} />

        <tbody>
          {mediaList.map((media) => (
            <ExploreSitesTableRow
              media={media}
              key={media.id}
              campaignId={campaignId}
              isOrgMedia={isOrgMedia}
              isPatrikaSpecific={isPatrikaSpecific}
              citySummary={citySummary}
            />
          ))}
        </tbody>
      </table>

      <Pagination
        totalCount={totalCount}
        pageNo={pageNo}
        pageSize={pageSize}
        onClick={loadCityMedia}
      />
    </div>
  );
}

export default ExploreSitesTable;
